import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './SchoolDetail.css';
import '../../components/DashboardSchoolTeacher/DashboardSchoolTeacher.css';
import { supabase } from '../../lib/supabase';
import { syncBranchesProvidersFromParent } from '../../lib/syncBranchProviders';
import { logSchoolActivity, logUserActivity } from '../../lib/activityLogger';
import { UserIdGenerator } from '../../lib/userIdGenerator';
import NotificationModal from '../../components/NotificationModal/NotificationModal';
import DashboardSchoolCalendar from '../../components/DashboardSchoolCalendar/DashboardSchoolCalendar';
import DashboardSchoolBranches from '../../components/DashboardSchoolBranches/DashboardSchoolBranches';
import * as XLSX from 'xlsx';

// Helper function to convert database errors to user-friendly messages
const getFriendlyErrorMessage = (error, operation = 'operation') => {
  if (!error) return 'An unexpected error occurred.';
  
  const errorMessage = error.message || String(error);
  const errorCode = error.code || '';
  
  // Duplicate email error
  if (
    errorMessage.includes('users_school_id_email_key') ||
    (errorMessage.includes('duplicate key') && errorMessage.includes('email'))
  ) {
    return 'This email address is already registered for a student or teacher in this school. Please use a different email address.';
  }
  
  // Duplicate user_id error
  if (
    errorMessage.includes('users_user_id_key') ||
    (errorMessage.includes('duplicate key') && errorMessage.includes('user_id'))
  ) {
    return 'A user with this ID already exists. Please try again.';
  }
  
  // Foreign key constraint errors
  if (errorMessage.includes('foreign key constraint') || errorMessage.includes('violates foreign key')) {
    if (errorMessage.includes('school_id')) {
      return 'Invalid school selected. Please refresh the page and try again.';
    }
    if (errorMessage.includes('grade_id')) {
      return 'Invalid grade selected. Please select a valid grade.';
    }
    if (errorMessage.includes('grade_section_id')) {
      return 'Invalid grade section selected. Please select a valid section.';
    }
    return 'Invalid data provided. Please check your selections and try again.';
  }
  
  // Not null constraint errors
  if (errorMessage.includes('null value') && errorMessage.includes('violates not-null constraint')) {
    return 'Please fill in all required fields.';
  }
  
  // Network/connection errors
  if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorCode === 'PGRST116') {
    return 'Network error. Please check your internet connection and try again.';
  }
  
  // Return original message if no specific pattern matches
  return errorMessage;
};

const SchoolDetail = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();

  // State management
  const [school, setSchool] = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [grades, setGrades] = useState([]);
  const [gradeSections, setGradeSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Form states
  const [isEditing, setIsEditing] = useState(false);
  const [schoolFormData, setSchoolFormData] = useState({
    name: '',
    school_id: '',
    password: '',
    max_students: '',
    max_teachers: '',
    subscription_start_date: '',
    subscription_end_date: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    website: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    selectedProviders: []
  });

  // Modal states
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddTeacherModal, setShowAddTeacherModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '' },
    { id: 'users', label: 'Users', icon: '' },
    { id: 'teachers', label: 'Teachers', icon: '' },
    { id: 'calendar', label: 'Calendar', icon: '' },
    { id: 'grades', label: 'Grades', icon: '' },
    { id: 'subjects', label: 'Subjects', icon: '' },
    { id: 'providers', label: 'Providers', icon: '' },
    // Only show Branches tab if school is loaded and is not a branch itself
    ...(school && !school.parent_school_id ? [{ id: 'branches', label: 'Branches', icon: '' }] : [])
  ];

  // Fetch school data
  useEffect(() => {
    if (schoolId) {
      fetchSchoolData();
    }
  }, [schoolId]);

  const fetchSchoolData = async () => {
    try {
      setLoading(true);

      // Fetch school
      const { data: schoolData, error: schoolError } = await supabase
        .from('schools')
        .select('*')
        .eq('id', schoolId)
        .single();

      if (schoolError) throw schoolError;

      // If this is a branch and has no providers, inherit from parent so branches always match parent's providers
      let schoolToUse = schoolData;
      if (schoolData.parent_school_id) {
        const branchProviders = schoolData.accessible_providers;
        const hasNoProviders = !branchProviders || (Array.isArray(branchProviders) && branchProviders.length === 0);
        if (hasNoProviders) {
          const { data: parentSchool } = await supabase
            .from('schools')
            .select('accessible_providers')
            .eq('id', schoolData.parent_school_id)
            .single();
          const parentProviders = parentSchool?.accessible_providers || [];
          if (parentProviders.length > 0) {
            await supabase
              .from('schools')
              .update({ accessible_providers: parentProviders })
              .eq('id', schoolId);
            schoolToUse = { ...schoolData, accessible_providers: parentProviders };
          }
        }
      }

      setSchool(schoolToUse);

      // If this is the parent school, sync its branches' providers (fixes pre-update branches)
      if (!schoolToUse.parent_school_id) {
        syncBranchesProvidersFromParent(schoolId).catch(() => {});
      }

      // Parse contact and owner data
      const contact = schoolToUse.contact || {};
      const owner = schoolToUse.owner || {};

      setSchoolFormData({
        name: schoolToUse.name || '',
        school_id: schoolToUse.school_id || '',
        password: schoolToUse.password || '',
        max_students: schoolToUse.max_students ?? '',
        max_teachers: schoolToUse.max_teachers ?? '',
        subscription_start_date: schoolToUse.subscription_start_date || '',
        subscription_end_date: schoolToUse.subscription_end_date || '',
        address: contact.address || '',
        city: contact.city || '',
        state: contact.state || '',
        zipCode: contact.zipCode || '',
        phone: contact.phone || '',
        email: contact.email || '',
        website: contact.website || '',
        ownerName: owner.name || '',
        ownerEmail: owner.email || '',
        ownerPhone: owner.phone || '',
        selectedProviders: schoolToUse.accessible_providers || []
      });

      // Fetch related data
      await Promise.all([
        fetchStudents(),
        fetchTeachers(),
        fetchGrades(),
        fetchGradeSections(),
        fetchSubjects(),
        fetchProviders()
      ]);
    } catch (err) {showNotification('error', 'Error Loading School', err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async (params = {}) => {
    const { searchTerm, gradeId, subsectionId } = params;
    try {
      let query = supabase
        .from('users')
        .select('*')
        .eq('school_id', schoolId)
        .eq('role', 'student');

      if (searchTerm) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      // Filter by grade_id or graduated status
      if (gradeId === 'graduated') {
        // Filter for graduated students (is_graduated is not null)
        query = query.not('is_graduated', 'is', null);
      } else if (gradeId) {
        query = query.eq('grade_id', gradeId);
      }

      // Filter by subsection_id (only if not filtering by graduated)
      if (subsectionId && gradeId !== 'graduated') {
        query = query.eq('grade_section_id', subsectionId);
      }

      const { data, error } = await query.order('full_name', { ascending: true });

      if (error) throw error;
      setStudents(data || []);
    } catch (err) {
      }
  };

  const fetchTeachers = async (params = {}) => {
    try {
      const { searchTerm, role, subjectId, gradeId, subsectionId } = params;
      let query = supabase
        .from('users')
        .select('*')
        .eq('school_id', schoolId)
        .eq('role', 'teacher');

      if (searchTerm) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      if (role) {
        query = query.eq('teacher_role', role);
      }

      if (subjectId) {
        // Here we filter by the 'subject' column which contains the subject name or by joining teacher_subjects
        // For simplicity and matching current schema usage, we'll try to match the subject ID in users table if applicable
        // Or if 'subject' text column is used. In this case, we'll use the ID filter for consistency if the field is uuid.
        query = query.eq('subject', subjectId);
      }

      const { data, error } = await query.order('full_name', { ascending: true });

      if (error) throw error;

      // Client-side filtering for grade (handles teachers_grade_ids array)
      let filteredData = data || [];

      if ((gradeId || subsectionId) && data) {
        filteredData = data.filter(teacher => {
          // Check if teacher has this grade in teachers_grade_ids array
          if (teacher.teachers_grade_ids && Array.isArray(teacher.teachers_grade_ids)) {
            return teacher.teachers_grade_ids.some(item => {
              // Handle new object format: {grade_id, section_id}
              if (typeof item === 'object' && item.grade_id) {
                if (subsectionId) {
                  return item.grade_id === gradeId && item.section_id === subsectionId;
                }
                return item.grade_id === gradeId;
              }
              // Handle legacy string format
              if (typeof item === 'string') {
                if (subsectionId) return false;
                return item === gradeId || item.startsWith(`${gradeId}:`);
              }
              return false;
            });
          }
          // Fallback: check grade text field
          if (teacher.grade && !subsectionId) {
            const selectedGrade = grades.find(g => g.id === gradeId);
            if (selectedGrade) {
              const gradeList = teacher.grade.split(',').map(g => g.trim());
              return gradeList.some(g => g.includes(selectedGrade.name));
            }
          }
          return false;
        });
      }

      setTeachers(filteredData);
    } catch (err) {
      }
  };

  const fetchGrades = async () => {
    try {
      const { data, error } = await supabase
        .from('grades')
        .select('*')
        .eq('school_id', schoolId)
        .order('order_index', { ascending: true });

      if (error) throw error;
      setGrades(data || []);
    } catch (err) {
      }
  };

  const fetchGradeSections = async () => {
    try {
      // First get all grade IDs for this school
      const { data: gradesData, error: gradesError } = await supabase
        .from('grades')
        .select('id')
        .eq('school_id', schoolId);

      if (gradesError) throw gradesError;

      if (!gradesData || gradesData.length === 0) {
        setGradeSections([]);
        return;
      }

      const gradeIds = gradesData.map(g => g.id);

      // Then fetch sections for those grades
      const { data, error } = await supabase
        .from('grade_sections')
        .select('*')
        .in('grade_id', gradeIds)
        .order('order_index', { ascending: true });

      if (error) throw error;
      setGradeSections(data || []);
    } catch (err) {
      }
  };

  const fetchSubjects = async () => {
    try {
      const { data, error } = await supabase
        .from('subjects')
        .select('*')
        .eq('school_id', schoolId)
        .order('name', { ascending: true });

      if (error) throw error;
      setSubjects(data || []);
    } catch (err) {
      }
  };

  const fetchProviders = async () => {
    try {
      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setProviders(data || []);
    } catch (err) {
      }
  };

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSchoolFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateSchool = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const contact = {
        address: schoolFormData.address,
        city: schoolFormData.city,
        state: schoolFormData.state,
        zipCode: schoolFormData.zipCode,
        phone: schoolFormData.phone,
        email: schoolFormData.email,
        website: schoolFormData.website
      };

      const owner = {
        name: schoolFormData.ownerName,
        email: schoolFormData.ownerEmail,
        phone: schoolFormData.ownerPhone
      };

      const { error } = await supabase
        .from('schools')
        .update({
          name: schoolFormData.name,
          password: schoolFormData.password,
          max_students: schoolFormData.max_students !== '' ? Number(schoolFormData.max_students) : null,
          max_teachers: schoolFormData.max_teachers !== '' ? Number(schoolFormData.max_teachers) : null,
          subscription_start_date: schoolFormData.subscription_start_date || null,
          subscription_end_date: schoolFormData.subscription_end_date || null,
          contact,
          owner,
          accessible_providers: schoolFormData.selectedProviders
        })
        .eq('id', schoolId);

      if (error) throw error;

      await logSchoolActivity.updated(schoolFormData.name);
      showNotification('success', 'School Updated', 'School information has been updated successfully');
      setIsEditing(false);
      fetchSchoolData();
    } catch (err) {showNotification('error', 'Error Updating School', err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProviderToggle = (providerId) => {
    setSchoolFormData(prev => ({
      ...prev,
      selectedProviders: prev.selectedProviders.includes(providerId)
        ? prev.selectedProviders.filter(id => id !== providerId)
        : [...prev.selectedProviders, providerId]
    }));
  };

  if (loading) {
    return (
      <div className="school-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading school data...</p>
      </div>
    );
  }

  if (!school) {
    return (
      <div className="school-detail-error">
        <h2>School not found</h2>
        <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
      </div>
    );
  }

  return (
    <div className="school-detail-page">
      <div className="school-detail-header">
        <div className="header-left-group">
          <button className="back-button" onClick={() => navigate('/dashboard')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" />
            </svg>
            Back to Dashboard
          </button>
          <div className="header-school-stats">
            <div className="header-stat">
              <span className="header-stat-value">{students.length}</span>
              <span className="header-stat-label">Students</span>
            </div>
            <div className="header-stat">
              <span className="header-stat-value">{teachers.length}</span>
              <span className="header-stat-label">Teachers</span>
            </div>
            <div className="header-stat">
              <span className="header-stat-value">{grades.length}</span>
              <span className="header-stat-label">Grades</span>
            </div>
            <div className="header-stat">
              <span className="header-stat-value">{subjects.length}</span>
              <span className="header-stat-label">Subjects</span>
            </div>
          </div>
        </div>
        <div className="school-header-info">
          <h1>{school.name}</h1>
          <span className="school-id-badge">ID: {school.school_id}</span>
        </div>
      </div>

      <div className="school-detail-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="school-detail-content">
        {activeTab === 'overview' && (
          <OverviewTab
            school={school}
            schoolFormData={schoolFormData}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            handleInputChange={handleInputChange}
            handleUpdateSchool={handleUpdateSchool}
            isSubmitting={isSubmitting}
            providers={providers}
            handleProviderToggle={handleProviderToggle}
            students={students}
            teachers={teachers}
            grades={grades}
            subjects={subjects}
          />
        )}

        {activeTab === 'users' && (
          <UsersTab
            students={students}
            onRefresh={fetchStudents}
            schoolId={schoolId}
            schoolCode={school?.school_id}
            showNotification={showNotification}
            grades={grades}
            gradeSections={gradeSections}
          />
        )}

        {activeTab === 'teachers' && (
          <TeachersTab
            teachers={teachers}
            onRefresh={fetchTeachers}
            schoolId={schoolId}
            schoolCode={school?.school_id}
            showNotification={showNotification}
            subjects={subjects}
            grades={grades}
            gradeSections={gradeSections}
          />
        )}

        {activeTab === 'grades' && (
          <GradesTab
            grades={grades}
            gradeSections={gradeSections}
            schoolId={schoolId}
            onRefresh={() => {
              fetchGrades();
              fetchGradeSections();
            }}
            showNotification={showNotification}
          />
        )}

        {activeTab === 'subjects' && (
          <SubjectsTab
            subjects={subjects}
            schoolId={schoolId}
            onRefresh={fetchSubjects}
            showNotification={showNotification}
          />
        )}

        {activeTab === 'providers' && (
          <ProvidersTab
            school={school}
            providers={providers}
            selectedProviders={schoolFormData.selectedProviders}
            onRefresh={fetchSchoolData}
            isSubmitting={isSubmitting}
            showNotification={showNotification}
          />
        )}

        {activeTab === 'calendar' && (
          <div className="calendar-tab-container">
            <DashboardSchoolCalendar
              schoolId={schoolId}
              grades={grades}
              gradeSections={gradeSections}
              teachers={teachers}
              readOnly={false}
            />
          </div>
        )}

        {activeTab === 'branches' && (
          <div className="branches-tab-container">
            <DashboardSchoolBranches
              schoolData={school}
              dataLoading={loading}
              onRefresh={fetchSchoolData}
            />
          </div>
        )}
      </div>

      <NotificationModal
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={closeNotification}
      />
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({
  school,
  schoolFormData,
  isEditing,
  setIsEditing,
  handleInputChange,
  handleUpdateSchool,
  isSubmitting,
  providers,
  handleProviderToggle,
  students,
  teachers,
  grades,
  subjects
}) => {
  if (isEditing) {
    return (
      <div className="overview-tab">
        <div className="tab-section-header">
          <h2>Edit School Information</h2>
          <button className="cancel-edit-btn" onClick={() => setIsEditing(false)}>Cancel</button>
        </div>
        <form onSubmit={handleUpdateSchool} className="school-edit-form">
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>School Name *</label>
                <input type="text" name="name" value={schoolFormData.name} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input type="text" name="password" value={schoolFormData.password} onChange={handleInputChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Max Students</label>
                <input type="number" name="max_students" value={schoolFormData.max_students} onChange={handleInputChange} min="0" />
              </div>
              <div className="form-group">
                <label>Max Teachers</label>
                <input type="number" name="max_teachers" value={schoolFormData.max_teachers} onChange={handleInputChange} min="0" />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Subscription</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="subscription_start_date"
                  value={schoolFormData.subscription_start_date || ''}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  name="subscription_end_date"
                  value={schoolFormData.subscription_end_date || ''}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            {schoolFormData.subscription_start_date && schoolFormData.subscription_end_date && (() => {
              const start = new Date(schoolFormData.subscription_start_date);
              const end = new Date(schoolFormData.subscription_end_date);
              const diffMs = end - start;
              if (diffMs <= 0) return null;
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              let durationText = '';
              if (diffDays >= 365) {
                const years = Math.floor(diffDays / 365);
                const remainingDays = diffDays % 365;
                const months = Math.floor(remainingDays / 30);
                durationText = years === 1 ? '1 year' : `${years} years`;
                if (months > 0) durationText += ` ${months} month${months > 1 ? 's' : ''}`;
              } else if (diffDays >= 30) {
                const months = Math.floor(diffDays / 30);
                const remainingDays = diffDays % 30;
                durationText = months === 1 ? '1 month' : `${months} months`;
                if (remainingDays >= 7) {
                  const weeks = Math.floor(remainingDays / 7);
                  durationText += ` ${weeks} week${weeks > 1 ? 's' : ''}`;
                }
              } else if (diffDays >= 7) {
                const weeks = Math.floor(diffDays / 7);
                const remainingDays = diffDays % 7;
                durationText = weeks === 1 ? '1 week' : `${weeks} weeks`;
                if (remainingDays > 0) durationText += ` ${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
              } else {
                durationText = diffDays === 1 ? '1 day' : `${diffDays} days`;
              }
              return <div className="field-hint" style={{ marginTop: '-12px', marginBottom: '24px', color: '#667085', fontWeight: '500' }}>Duration: {durationText}</div>;
            })()}
          </div>

          <div className="form-section">
            <h3>Contact Information</h3>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Address *</label>
                <textarea name="address" value={schoolFormData.address} onChange={handleInputChange} rows="3" required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input type="text" name="city" value={schoolFormData.city} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>State</label>
                <input type="text" name="state" value={schoolFormData.state} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>ZIP Code</label>
                <input type="text" name="zipCode" value={schoolFormData.zipCode} onChange={handleInputChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone *</label>
                <input type="tel" name="phone" value={schoolFormData.phone} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={schoolFormData.email} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input type="url" name="website" value={schoolFormData.website} onChange={handleInputChange} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Owner Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Owner Name *</label>
                <input type="text" name="ownerName" value={schoolFormData.ownerName} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Owner Email</label>
                <input type="email" name="ownerEmail" value={schoolFormData.ownerEmail} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Owner Phone *</label>
                <input type="tel" name="ownerPhone" value={schoolFormData.ownerPhone} onChange={handleInputChange} required />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Learning Providers</h3>
            <div className="providers-grid">
              {providers.map(provider => (
                <label key={provider.id} className="provider-checkbox">
                  <input
                    type="checkbox"
                    checked={schoolFormData.selectedProviders.includes(provider.id)}
                    onChange={() => handleProviderToggle(provider.id)}
                  />
                  <span>{provider.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={() => setIsEditing(false)}>Cancel</button>
            <button type="submit" className="save-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form >
      </div >
    );
  }

  return (
    <div className="overview-tab">
      <div className="tab-section-header">
        <h2>School Overview</h2>
        <button className="edit-btn" onClick={() => setIsEditing(true)}>Edit</button>
      </div>

      {/* Progress Charts Section */}
      <div className="progress-charts-section">
        {/* Subscription Time Chart */}
        {(() => {
          const startDate = school.subscription_start_date ? new Date(school.subscription_start_date) : null;
          const endDate = school.subscription_end_date ? new Date(school.subscription_end_date) : null;
          const now = new Date();

          if (startDate && endDate) {
            const totalDuration = endDate - startDate;
            const elapsed = now - startDate;
            const percentage = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
            const daysLeft = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
            const totalDays = Math.ceil(totalDuration / (1000 * 60 * 60 * 24));
            const isExpired = now > endDate;

            return (
              <div className="progress-chart-card">
                <div className="circular-progress" style={{ '--progress': percentage, '--color': isExpired ? '#ef4444' : percentage > 80 ? '#f59e0b' : '#10b981' }}>
                  <svg viewBox="0 0 100 100">
                    <circle className="progress-bg" cx="50" cy="50" r="45" />
                    <circle className="progress-fill" cx="50" cy="50" r="45" />
                  </svg>
                  <div className="progress-value">{Math.round(percentage)}%</div>
                </div>
                <div className="progress-info">
                  <h4>Subscription Time</h4>
                  <p className="progress-detail">{isExpired ? 'Expired' : `${daysLeft} days left`}</p>
                  <p className="progress-subtext">{totalDays} days total</p>
                </div>
              </div>
            );
          }
          return (
            <div className="progress-chart-card">
              <div className="circular-progress no-data-chart">
                <svg viewBox="0 0 100 100">
                  <circle className="progress-bg" cx="50" cy="50" r="45" />
                </svg>
                <div className="progress-value">—</div>
              </div>
              <div className="progress-info">
                <h4>Subscription Time</h4>
                <p className="progress-detail">No dates set</p>
              </div>
            </div>
          );
        })()}

        {/* Students Chart */}
        {(() => {
          const activeStudents = students.filter(s => s.is_active !== false && s.status !== 'graduated');
          const inactiveStudents = students.filter(s => s.is_active === false);
          const graduatedStudents = students.filter(s => s.status === 'graduated');
          const current = activeStudents.length;
          const max = school.max_students;
          const percentage = max ? Math.min(100, (current / max) * 100) : 0;
          const hasMax = max !== null && max !== undefined && max > 0;

          return (
            <div className="progress-chart-card">
              <div className="circular-progress" style={{ '--progress': hasMax ? percentage : 0, '--color': percentage > 90 ? '#ef4444' : percentage > 70 ? '#f59e0b' : '#3b82f6' }}>
                <svg viewBox="0 0 100 100">
                  <circle className="progress-bg" cx="50" cy="50" r="45" />
                  {hasMax && <circle className="progress-fill" cx="50" cy="50" r="45" />}
                </svg>
                <div className="progress-value">{hasMax ? `${Math.round(percentage)}%` : current}</div>
              </div>
              <div className="progress-info">
                <h4>Students</h4>
                <p className="progress-detail">{current} active / {hasMax ? max : '∞'}</p>
                <p className="progress-subtext">{hasMax ? `${Math.max(0, max - current)} slots available` : 'No limit set'}</p>
                {(inactiveStudents.length > 0 || graduatedStudents.length > 0) && (
                  <p className="progress-extra">
                    {inactiveStudents.length > 0 && <span className="inactive-count">{inactiveStudents.length} inactive</span>}
                    {inactiveStudents.length > 0 && graduatedStudents.length > 0 && ' • '}
                    {graduatedStudents.length > 0 && <span className="graduated-count">{graduatedStudents.length} graduated</span>}
                  </p>
                )}
              </div>
            </div>
          );
        })()}

        {/* Teachers Chart */}
        {(() => {
          const activeTeachers = teachers.filter(t => t.is_active !== false);
          const inactiveTeachers = teachers.filter(t => t.is_active === false);
          const current = activeTeachers.length;
          const max = school.max_teachers;
          const percentage = max ? Math.min(100, (current / max) * 100) : 0;
          const hasMax = max !== null && max !== undefined && max > 0;

          return (
            <div className="progress-chart-card">
              <div className="circular-progress" style={{ '--progress': hasMax ? percentage : 0, '--color': percentage > 90 ? '#ef4444' : percentage > 70 ? '#f59e0b' : '#8b5cf6' }}>
                <svg viewBox="0 0 100 100">
                  <circle className="progress-bg" cx="50" cy="50" r="45" />
                  {hasMax && <circle className="progress-fill" cx="50" cy="50" r="45" />}
                </svg>
                <div className="progress-value">{hasMax ? `${Math.round(percentage)}%` : current}</div>
              </div>
              <div className="progress-info">
                <h4>Teachers</h4>
                <p className="progress-detail">{current} active / {hasMax ? max : '∞'}</p>
                <p className="progress-subtext">{hasMax ? `${Math.max(0, max - current)} slots available` : 'No limit set'}</p>
                {inactiveTeachers.length > 0 && (
                  <p className="progress-extra">
                    <span className="inactive-count">{inactiveTeachers.length} inactive</span>
                  </p>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      <div className="details-section">
        <h3>Basic Information</h3>
        <div className="details-grid">
          <div className="detail-item">
            <span className="detail-label">School Name</span>
            <span className="detail-value">{school.name || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">School ID</span>
            <span className="detail-value">{school.school_id || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">School Password</span>
            <span className="detail-value">{school.password || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Max Students</span>
            <span className="detail-value">{school.max_students ?? '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Max Teachers</span>
            <span className="detail-value">{school.max_teachers ?? '—'}</span>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h3>Contact Information</h3>
        <div className="details-grid">
          <div className="detail-item full-width">
            <span className="detail-label">Address</span>
            <span className="detail-value">{school.contact?.address || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">City</span>
            <span className="detail-value">{school.contact?.city || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">State</span>
            <span className="detail-value">{school.contact?.state || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">ZIP Code</span>
            <span className="detail-value">{school.contact?.zipCode || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Phone</span>
            <span className="detail-value">{school.contact?.phone || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Email</span>
            <span className="detail-value">{school.contact?.email || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Website</span>
            <span className="detail-value">{school.contact?.website || '—'}</span>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h3>Owner Information</h3>
        <div className="details-grid">
          <div className="detail-item">
            <span className="detail-label">Name</span>
            <span className="detail-value">{school.owner?.name || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Email</span>
            <span className="detail-value">{school.owner?.email || '—'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Phone</span>
            <span className="detail-value">{school.owner?.phone || '—'}</span>
          </div>
        </div>
      </div>
    </div >
  );
};

// Users Tab Component - will be implemented with CRUD operations
const UsersTab = ({ students, onRefresh, schoolId, schoolCode, showNotification, grades, gradeSections }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [systemLanguages, setSystemLanguages] = useState([]);
  const [filterGradeId, setFilterGradeId] = useState('');
  const [filterSubsectionId, setFilterSubsectionId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [userFormData, setUserFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    grade_id: '',
    grade_section_id: '',
    language: '',
    is_active: true
  });
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [newPassword, setNewPassword] = useState(''); // Separate state for password editing
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Helper to refresh with current search and filter criteria
  const refreshList = () => {
    return onRefresh({
      searchTerm: searchTerm.trim(),
      gradeId: filterGradeId,
      subsectionId: filterSubsectionId
    });
  };

  // Debounced search and filter effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setIsSearching(true);
      refreshList().finally(() => setIsSearching(false));
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm, filterGradeId, filterSubsectionId]);

  // Get available subsections for selected grade
  const getAvailableSubsections = () => {
    if (!userFormData.grade_id) return [];
    return gradeSections
      .filter(section => section.grade_id === userFormData.grade_id && section.is_active)
      .sort((a, b) => a.order_index - b.order_index);
  };

  const availableSubsections = getAvailableSubsections();

  // Password handlers
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    // Only allow numbers for password field
    const numericValue = value.replace(/\D/g, '').slice(0, 6); // Limit to 6 digits
    setNewPassword(numericValue);
  };

  const handlePasswordSquareClick = () => {
    const hiddenInput = document.getElementById('school-detail-users-password-input');
    if (hiddenInput) {
      hiddenInput.focus();
    }
  };

  // Build list of grade/section options for display (similar to DashboardSchoolStudents)
  const buildGradeSectionOptions = () => {
    const options = [];

    (grades || [])
      .filter(g => g.is_active)
      .sort((a, b) => a.order_index - b.order_index)
      .forEach(grade => {
        const sections = (gradeSections || [])
          .filter(s => s.grade_id === grade.id && s.is_active)
          .sort((a, b) => a.order_index - b.order_index);

        if (sections.length > 0) {
          sections.forEach(section => {
            options.push({
              key: `${grade.id}:${section.id}`,
              label: `${grade.name} ${section.name}`,
              gradeId: grade.id,
              sectionId: section.id,
              isSection: true
            });
          });
        } else {
          options.push({
            key: grade.id,
            label: grade.name,
            gradeId: grade.id,
            sectionId: null,
            isSection: false
          });
        }
      });

    return options;
  };

  const getSelectedGradeChoiceValue = () => {
    if (!userFormData.grade_id) return '';
    if (userFormData.grade_section_id) {
      return `${userFormData.grade_id}:${userFormData.grade_section_id}`;
    }
    return userFormData.grade_id;
  };

  const handleGradeChoiceChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setUserFormData(prev => ({ ...prev, grade_id: '', grade_section_id: '' }));
      return;
    }

    if (value.includes(':')) {
      const [gradeId, sectionId] = value.split(':');
      setUserFormData(prev => ({ ...prev, grade_id: gradeId, grade_section_id: sectionId }));
      return;
    }

    setUserFormData(prev => ({ ...prev, grade_id: value, grade_section_id: '' }));
  };

  // Check if form has been modified
  const hasFormChanges = () => {
    if (!initialFormData) return false;
    return JSON.stringify(userFormData) !== JSON.stringify(initialFormData);
  };

  // Handle overlay click with unsaved changes check
  const handleOverlayClick = (closeAction) => {
    if (hasFormChanges()) {
      setPendingCloseAction(() => closeAction);
      setShowDiscardConfirm(true);
    } else {
      closeAction();
    }
  };

  // Confirm discard changes
  const confirmDiscard = () => {
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
    setShowDiscardConfirm(false);
    setInitialFormData(null);
  };

  // Cancel discard
  const cancelDiscard = () => {
    setShowDiscardConfirm(false);
    setPendingCloseAction(null);
  };

  // Set initial form data when modal opens
  useEffect(() => {
    if (showAddModal) {
      setInitialFormData(JSON.parse(JSON.stringify(userFormData)));
    } else {
      setInitialFormData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddModal]);

  // Fetch system languages
  useEffect(() => {
    fetchSystemLanguages();
  }, []);

  // When grade changes, auto-select first subsection if available
  useEffect(() => {
    if (!userFormData.grade_id) {
      setUserFormData(prev => {
        if (prev.grade_section_id) {
          return { ...prev, grade_section_id: '' };
        }
        return prev;
      });
      return;
    }

    const subsections = gradeSections
      .filter(section => section.grade_id === userFormData.grade_id && section.is_active)
      .sort((a, b) => a.order_index - b.order_index);

    if (subsections.length > 0) {
      setUserFormData(prev => {
        if (!prev.grade_section_id) {
          return { ...prev, grade_section_id: subsections[0].id };
        }
        const isValidSection = subsections.some(s => s.id === prev.grade_section_id);
        if (!isValidSection) {
          return { ...prev, grade_section_id: subsections[0].id };
        }
        return prev;
      });
    } else {
      setUserFormData(prev => {
        if (prev.grade_section_id) {
          return { ...prev, grade_section_id: '' };
        }
        return prev;
      });
    }
  }, [userFormData.grade_id, gradeSections]);

  const fetchSystemLanguages = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('available_languages')
        .eq('id', 1)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setSystemLanguages(data?.available_languages || []);
    } catch (err) {setSystemLanguages(['English', 'French', 'Arabic']);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const userId = await UserIdGenerator.generateUserId(schoolCode);
      if (!userId) {
        showNotification('error', 'Error', 'Failed to generate user ID');
        return;
      }

      const randomPassword = Math.floor(100000 + Math.random() * 900000).toString();

      const selectedGrade = grades.find(g => g.id === userFormData.grade_id);
      const gradeName = selectedGrade ? selectedGrade.name : null;

      const { error } = await supabase
        .from('users')
        .insert([{
          user_id: userId,
          first_name: userFormData.first_name,
          last_name: userFormData.last_name,
          email: userFormData.email,
          phone: userFormData.phone || null,
          school_id: schoolId,
          role: 'student',
          grade: gradeName,
          grade_id: userFormData.grade_id || null,
          grade_section_id: userFormData.grade_section_id || null,
          language: userFormData.language || null,
          is_active: userFormData.is_active,
          password: randomPassword
        }]);

      if (error) {
        const friendlyMessage = getFriendlyErrorMessage(error, 'adding user');
        showNotification('error', 'Error Adding User', friendlyMessage);
        return;
      }

      showNotification('success', 'User Added', `User ${userId} added with password: ${randomPassword}`);
      setShowAddModal(false);
      setUserFormData({
        first_name: '', last_name: '', email: '', phone: '', grade_id: '', grade_section_id: '', language: '', is_active: true
      });
      setInitialFormData(null);
      refreshList();
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err, 'adding user');
      showNotification('error', 'Error Adding User', friendlyMessage);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      const selectedGrade = grades.find(g => g.id === userFormData.grade_id);
      const gradeName = selectedGrade ? selectedGrade.name : null;

      const updatePayload = {
        first_name: userFormData.first_name,
        last_name: userFormData.last_name,
        email: userFormData.email,
        phone: userFormData.phone || null,
        grade: gradeName,
        grade_id: userFormData.grade_id || null,
        grade_section_id: userFormData.grade_section_id || null,
        language: userFormData.language || null,
        is_active: userFormData.is_active
      };

      // Graduation rule:
      // - If the new grade is NOT the last grade in the school, the student must NOT be graduated.
      //   So we force is_graduated = null, and if they were graduated before, we re-activate them.
      try {
        const nextGradeId = updatePayload.grade_id;
        if (schoolId && nextGradeId) {
          const { data: allGrades, error: gradesError } = await supabase
            .from('grades')
            .select('id, order_index')
            .eq('school_id', schoolId)
            .eq('is_active', true)
            .order('order_index', { ascending: false })
            .limit(1);

          const lastGradeId = !gradesError && allGrades && allGrades.length ? allGrades[0].id : null;
          if (lastGradeId && String(nextGradeId) !== String(lastGradeId)) {
            updatePayload.is_graduated = null;
            if (editingUser?.is_graduated) updatePayload.is_active = true;
          }
        } else {
          // No grade selected means not in last grade => not graduated.
          updatePayload.is_graduated = null;
          if (editingUser?.is_graduated) updatePayload.is_active = true;
        }
      } catch (err) {
        updatePayload.is_graduated = null;
        if (editingUser?.is_graduated) updatePayload.is_active = true;}

      // Only update password if a new one is provided
      if (newPassword && newPassword.trim() !== '') {
        updatePayload.password = newPassword.trim();
      }

      const { error } = await supabase
        .from('users')
        .update(updatePayload)
        .eq('id', editingUser.id);

      if (error) {
        const friendlyMessage = getFriendlyErrorMessage(error, 'updating user');
        showNotification('error', 'Error Updating User', friendlyMessage);
        return;
      }

      const passwordMsg = newPassword && newPassword.trim() !== '' ? ' Password has been updated.' : '';
      showNotification('success', 'User Updated', `User information updated successfully.${passwordMsg}`);
      setEditingUser(null);
      setShowAddModal(false);
      setInitialFormData(null);
      setNewPassword(''); // Clear password field after successful update
      refreshList();
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err, 'updating user');
      showNotification('error', 'Error Updating User', friendlyMessage);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userToDelete);

      if (error) throw error;

      showNotification('success', 'User Deleted', 'User deleted successfully');
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      refreshList();
    } catch (err) {
      showNotification('error', 'Error Deleting User', err.message);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const confirmDeleteUser = (userId) => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };

  const cancelDeleteUser = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  const startEdit = (user) => {
    setEditingUser(user);
    const formData = {
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone: user.phone || '',
      grade_id: user.grade_id || '',
      grade_section_id: user.grade_section_id || '',
      language: user.language || '',
      is_active: user.is_active !== false
    };
    setUserFormData(formData);
    setInitialFormData(JSON.parse(JSON.stringify(formData)));
    setNewPassword(user.password || ''); // Load existing password when editing
    setShowAddModal(true);
  };

  // Get available subsections for the selected filter grade
  const getFilterSubsections = () => {
    if (!filterGradeId) return [];
    return gradeSections
      .filter(section => section.grade_id === filterGradeId && section.is_active)
      .sort((a, b) => a.order_index - b.order_index);
  };

  const filterSubsections = getFilterSubsections();

  return (
    <div className="users-tab">
      <div className="tab-section-header">
        <h2>Users ({students.length})</h2>
        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isSearching && (
              <div className="loading-inline" style={{ margin: '0 0 0 8px' }}></div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
            <select
              value={filterSubsectionId ? `${filterGradeId}:${filterSubsectionId}` : filterGradeId}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) {
                  setFilterGradeId('');
                  setFilterSubsectionId('');
                } else if (val === 'graduated') {
                  setFilterGradeId('graduated');
                  setFilterSubsectionId('');
                } else if (val.includes(':')) {
                  const [gId, sId] = val.split(':');
                  setFilterGradeId(gId);
                  setFilterSubsectionId(sId);
                } else {
                  setFilterGradeId(val);
                  setFilterSubsectionId('');
                }
              }}
              style={{
                padding: '10px 35px 10px 12px',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                outline: 'none',
                backgroundColor: '#f8f9fa',
                fontSize: '14px',
                color: '#333',
                minWidth: '150px',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                backgroundSize: '16px'
              }}
            >
              <option value="">All Grades</option>
              {grades.filter(g => g.is_active).sort((a, b) => a.order_index - b.order_index).map((grade) => {
                const sections = gradeSections
                  .filter(s => s.grade_id === grade.id && s.is_active)
                  .sort((a, b) => a.order_index - b.order_index);

                if (sections.length > 0) {
                  return sections.map(s => (
                    <option key={`${grade.id}:${s.id}`} value={`${grade.id}:${s.id}`}>
                      {grade.name} {s.name}
                    </option>
                  ));
                } else {
                  return <option key={grade.id} value={grade.id}>{grade.name}</option>;
                }
              })}
              <option value="graduated">Graduated</option>
            </select>

            {(filterGradeId || filterSubsectionId) && (
              <button
                onClick={() => {
                  setFilterGradeId('');
                  setFilterSubsectionId('');
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #dc3545',
                  backgroundColor: '#fff',
                  color: '#dc3545',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#fff5f5'}
                onMouseLeave={(e) => e.target.style.background = '#fff'}
              >
                Clear
              </button>
            )}
          </div>

          <button className="add-btn" onClick={() => {
            setEditingUser(null);
            const emptyForm = {
              first_name: '', last_name: '', email: '', phone: '', grade_id: '', grade_section_id: '', language: '', is_active: true
            };
            setUserFormData(emptyForm);
            setInitialFormData(JSON.parse(JSON.stringify(emptyForm)));
            setNewPassword(''); // Clear password when adding new user
            setShowAddModal(true);
          }}>
            + Add User
          </button>
        </div>
      </div>


      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>User ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Grade</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {isSearching ? 'Searching...' : 'No users found matching your criteria'}
                </td>
              </tr>
            ) : (
              students.map(user => (
                <tr key={user.id}>
                  <td>{user.user_id}</td>
                  <td>{user.full_name || `${user.first_name} ${user.last_name}`}</td>
                  <td>{user.email || '—'}</td>
                  <td>{user.grade || '—'}</td>
                  <td>
                    <span className={`status-badge ${user.is_graduated ? 'graduated' : (user.is_active ? 'active' : 'inactive')}`}>
                      {user.is_graduated ? 'Graduated' : (user.is_active ? 'Active' : 'Inactive')}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="edit-action-btn" onClick={() => startEdit(user)}>Edit</button>
                      <button className="delete-action-btn" onClick={() => confirmDeleteUser(user.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Discard Changes Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="unsaved-changes-overlay" onClick={cancelDiscard}>
          <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-changes-header">
              <div className="unsaved-changes-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Unsaved Changes</h2>
            </div>
            <div className="unsaved-changes-body">
              <p className="unsaved-changes-message">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>
            <div className="unsaved-changes-actions">
              <button className="unsaved-changes-cancel-btn" onClick={cancelDiscard}>
                Cancel
              </button>
              <button className="unsaved-changes-discard-btn" onClick={confirmDiscard}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="school-detail-users-delete-overlay" onClick={cancelDeleteUser}>
          <div className="school-detail-users-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="school-detail-users-delete-header">
              <div className="school-detail-users-delete-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Delete User</h2>
            </div>
            <div className="school-detail-users-delete-body">
              <p className="school-detail-users-delete-message">
                Are you sure you want to delete this user? This action cannot be undone.
              </p>
            </div>
            <div className="school-detail-users-delete-actions">
              <button className="school-detail-users-delete-cancel-btn" onClick={cancelDeleteUser}>
                Cancel
              </button>
              <button className="school-detail-users-delete-confirm-btn" onClick={handleDeleteUser}>
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => handleOverlayClick(() => {
          setShowAddModal(false);
          setEditingUser(null);
          setNewPassword(''); // Clear password on close
        })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
              <button className="modal-close" onClick={() => handleOverlayClick(() => {
                setShowAddModal(false);
                setEditingUser(null);
                setNewPassword(''); // Clear password on close
              })}>×</button>
            </div>
            <form onSubmit={editingUser ? handleUpdateUser : handleAddUser} className="user-form">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={userFormData.first_name}
                  onChange={(e) => setUserFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={userFormData.last_name}
                  onChange={(e) => setUserFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={userFormData.phone}
                  onChange={(e) => setUserFormData(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Grade *</label>
                <select
                  name="grade_choice"
                  value={getSelectedGradeChoiceValue()}
                  onChange={handleGradeChoiceChange}
                  required
                >
                  <option value="">Select grade</option>
                  {grades && grades.length > 0 ? (
                    buildGradeSectionOptions().map((opt) => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))
                  ) : (
                    <option value="" disabled>No grades available</option>
                  )}
                </select>
              </div>
              {editingUser && (
                <div className="form-group">
                  <label htmlFor="new_password">Change Password</label>
                  {/* Modern 6-square password UI */}
                  <div className="school-detail-users-password-squares-container">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <div
                        key={index}
                        className={`school-detail-users-password-square ${newPassword.length > index ? 'filled' : ''} ${newPassword.length === index ? 'active' : ''}`}
                        onClick={handlePasswordSquareClick}
                      >
                        {newPassword[index] || ''}
                      </div>
                    ))}
                  </div>
                  {/* Hidden input that actually controls the password value */}
                  <input
                    type="password"
                    id="school-detail-users-password-input"
                    name="new_password"
                    value={newPassword}
                    onChange={handlePasswordChange}
                    className="school-detail-users-password-hidden-input"
                    placeholder="Enter 6-digit password (numbers only)"
                    maxLength="6"
                    autoComplete="new-password"
                  />
                  <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
                    Password must be 6 digits (numbers only). Current password is shown. Edit to change it, or leave as-is to keep the same password.
                  </small>
                </div>
              )}
              {/* <div className="form-group">
                <label>Language</label>
                <select
                  value={userFormData.language}
                  onChange={(e) => setUserFormData(prev => ({ ...prev, language: e.target.value }))}
                >
                  <option value="">Select language</option>
                  {systemLanguages.map((language, index) => (
                    <option key={index} value={language}>{language}</option>
                  ))}
                </select>
              </div> */}
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={userFormData.is_active}
                    onChange={(e) => setUserFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span>Active</span>
                </label>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => handleOverlayClick(() => {
                  setShowAddModal(false);
                  setEditingUser(null);
                  setNewPassword(''); // Clear password on cancel
                })}>Cancel</button>
                <button type="submit" className="save-btn">{editingUser ? 'Update' : 'Add'} User</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Teachers Tab Component - similar to UsersTab
const TeachersTab = ({ teachers, onRefresh, schoolId, schoolCode, showNotification, subjects, grades, gradeSections }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterSubjectId, setFilterSubjectId] = useState('');
  const [filterGradeId, setFilterGradeId] = useState('');
  const [filterSubsectionId, setFilterSubsectionId] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [selectedGrades, setSelectedGrades] = useState(new Set());
  const [teacherFormData, setTeacherFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    teacher_role: '',
    subject: '',
    supervisor_id: '',
    is_active: true
  });
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [initialSelectedGrades, setInitialSelectedGrades] = useState(null);
  const [newPassword, setNewPassword] = useState(''); // Separate state for password editing
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState(null);

  // Parse teachers_grade_ids array - now supports objects with grade_id and section_id
  const parseGradesToSet = (teacher) => {
    if (!teacher) return new Set();

    const selectedKeys = new Set();

    // First try to use teachers_grade_ids array
    if (teacher.teachers_grade_ids && Array.isArray(teacher.teachers_grade_ids) && teacher.teachers_grade_ids.length > 0) {
      teacher.teachers_grade_ids.forEach(item => {
        if (typeof item === 'string') {
          // Legacy format: just grade_id as string
          selectedKeys.add(item);
        } else if (typeof item === 'object' && item.grade_id) {
          // New format: object with grade_id and optional section_id
          if (item.section_id) {
            selectedKeys.add(`${item.grade_id}:${item.section_id}`);
          } else {
            selectedKeys.add(item.grade_id);
          }
        }
      });
      return selectedKeys;
    }

    // Fallback: try to parse from grade string by matching grade names to IDs
    if (teacher.grade && grades) {
      const gradeString = teacher.grade;
      const parts = gradeString.split(',').map(g => g.trim()).filter(Boolean);
      parts.forEach(gradeName => {
        const grade = grades.find(g => g.name === gradeName);
        if (grade) {
          selectedKeys.add(grade.id);
        }
      });
    }

    return selectedKeys;
  };

  // Handle grade/section checkbox toggle
  // key can be "grade_id" for grade only, or "grade_id:section_id" for subsection
  const handleGradeToggle = (key) => {
    setSelectedGrades(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // If selecting a subsection, remove the parent grade if it exists
        if (key.includes(':')) {
          const [gradeId] = key.split(':');
          next.delete(gradeId); // Remove grade-only selection if subsection is selected
        } else {
          // If selecting a grade, remove all its subsections
          const gradeId = key;
          Array.from(next).forEach(existingKey => {
            if (existingKey.startsWith(`${gradeId}:`)) {
              next.delete(existingKey);
            }
          });
        }
        next.add(key);
      }
      return next;
    });
  };

  // Password handlers
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    // Only allow numbers for password field
    const numericValue = value.replace(/\D/g, '').slice(0, 6); // Limit to 6 digits
    setNewPassword(numericValue);
  };

  const handlePasswordSquareClick = () => {
    const hiddenInput = document.getElementById('school-detail-teachers-password-input');
    if (hiddenInput) {
      hiddenInput.focus();
    }
  };

  // Build list of grade/section options for display
  // Returns array of { key, label, gradeId, sectionId, isSection }
  const buildGradeSectionOptions = () => {
    const options = [];

    if (!grades) return options;

    grades
      .filter(g => g.is_active)
      .sort((a, b) => a.order_index - b.order_index)
      .forEach(grade => {
        // Get subsections for this grade
        const sections = gradeSections
          .filter(s => s.grade_id === grade.id && s.is_active)
          .sort((a, b) => a.order_index - b.order_index);

        if (sections.length > 0) {
          // If grade has subsections, show each subsection as a separate option
          sections.forEach(section => {
            options.push({
              key: `${grade.id}:${section.id}`,
              label: `${grade.name} ${section.name}`,
              gradeId: grade.id,
              sectionId: section.id,
              isSection: true
            });
          });
        } else {
          // If grade has no subsections, show grade as a single option
          options.push({
            key: grade.id,
            label: grade.name,
            gradeId: grade.id,
            sectionId: null,
            isSection: false
          });
        }
      });

    return options;
  };

  // Helper to refresh with current search and filter criteria
  const refreshList = () => {
    setIsSearching(true);
    onRefresh({
      searchTerm: searchTerm.trim(),
      role: filterRole,
      subjectId: filterSubjectId,
      gradeId: filterGradeId,
      subsectionId: filterSubsectionId
    }).finally(() => {
      setIsSearching(false);
    });
  };

  // Debounced search and filter
  useEffect(() => {
    const timer = setTimeout(() => {
      refreshList();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, filterRole, filterSubjectId, filterGradeId]);

  // Check if form has been modified
  const hasFormChanges = () => {
    if (!initialFormData) return false;
    const formChanged = JSON.stringify(teacherFormData) !== JSON.stringify(initialFormData);
    const gradesChanged = initialSelectedGrades === null 
      ? selectedGrades.size > 0
      : JSON.stringify(Array.from(selectedGrades).sort()) !== JSON.stringify(Array.from(initialSelectedGrades).sort());
    return formChanged || gradesChanged;
  };

  // Handle overlay click with unsaved changes check
  const handleOverlayClick = (closeAction) => {
    if (hasFormChanges()) {
      setPendingCloseAction(() => closeAction);
      setShowDiscardConfirm(true);
    } else {
      closeAction();
    }
  };

  // Confirm discard changes
  const confirmDiscard = () => {
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
    setShowDiscardConfirm(false);
    setInitialFormData(null);
    setInitialSelectedGrades(null);
  };

  // Cancel discard
  const cancelDiscard = () => {
    setShowDiscardConfirm(false);
    setPendingCloseAction(null);
  };

  // Set initial form data when modal opens
  useEffect(() => {
    if (showAddModal) {
      setInitialFormData(JSON.parse(JSON.stringify(teacherFormData)));
      setInitialSelectedGrades(new Set(selectedGrades));
    } else {
      setInitialFormData(null);
      setInitialSelectedGrades(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddModal]);

  const teacherRoles = [
    { value: '', label: 'No Role' },
    { value: 'curriculum_coordinator', label: 'Curriculum Coordinator' },
    { value: 'head_of_department', label: 'Head of Department' },
    { value: 'coordinator', label: 'Coordinator' },
    { value: 'teacher', label: 'Teacher' },
    { value: 'assistant', label: 'Assistant' }
  ];

  const handleAddTeacher = async (e) => {
    e.preventDefault();
    try {
      const userId = await UserIdGenerator.generateUserId(schoolCode);
      if (!userId) {
        showNotification('error', 'Error', 'Failed to generate user ID');
        return;
      }

      const randomPassword = Math.floor(100000 + Math.random() * 900000).toString();

      // Convert selected keys to object format for teachers_grade_ids
      const teachersGradeIds = Array.from(selectedGrades)
        .filter(Boolean)
        .map(key => {
          if (key.includes(':')) {
            // Subsection: format as {grade_id, section_id}
            const [gradeId, sectionId] = key.split(':');
            return { grade_id: gradeId, section_id: sectionId };
          } else {
            // Grade only: format as {grade_id, section_id: null}
            return { grade_id: key, section_id: null };
          }
        });

      // Also create grade string for backward compatibility
      const gradeNames = Array.from(selectedGrades)
        .filter(Boolean)
        .map(key => {
          const gradeId = key.includes(':') ? key.split(':')[0] : key;
          const grade = grades.find(g => g.id === gradeId);
          if (grade) {
            if (key.includes(':')) {
              const sectionId = key.split(':')[1];
              const section = gradeSections.find(s => s.id === sectionId);
              return section ? `${grade.name} ${section.name}` : grade.name;
            }
            return grade.name;
          }
          return null;
        })
        .filter(Boolean);
      const gradeString = gradeNames.length > 0 ? gradeNames.join(', ') : null;

      const { error } = await supabase
        .from('users')
        .insert([{
          user_id: userId,
          first_name: teacherFormData.first_name,
          last_name: teacherFormData.last_name,
          email: teacherFormData.email,
          phone: teacherFormData.phone || null,
          school_id: schoolId,
          role: 'teacher',
          teacher_role: teacherFormData.teacher_role && teacherFormData.teacher_role.trim() !== '' ? teacherFormData.teacher_role : null,
          subject: teacherFormData.subject || null,
          supervisor_id: teacherFormData.supervisor_id || null,
          grade: gradeString,
          teachers_grade_ids: teachersGradeIds.length > 0 ? teachersGradeIds : null,
          is_active: teacherFormData.is_active,
          password: randomPassword
        }]);

      if (error) {
        const friendlyMessage = getFriendlyErrorMessage(error, 'adding teacher');
        showNotification('error', 'Error Adding Teacher', friendlyMessage);
        return;
      }

      showNotification('success', 'Teacher Added', `Teacher ${userId} added with password: ${randomPassword}`);
      setShowAddModal(false);
      setTeacherFormData({
        first_name: '', last_name: '', email: '', phone: '', teacher_role: '', subject: '', supervisor_id: '', is_active: true
      });
      setSelectedGrades(new Set());
      setInitialFormData(null);
      setInitialSelectedGrades(null);
      onRefresh();
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err, 'adding teacher');
      showNotification('error', 'Error Adding Teacher', friendlyMessage);
    }
  };

  const handleUpdateTeacher = async (e) => {
    e.preventDefault();
    if (!editingTeacher) return;

    try {
      // Convert selected keys to object format for teachers_grade_ids
      const teachersGradeIds = Array.from(selectedGrades)
        .filter(Boolean)
        .map(key => {
          if (key.includes(':')) {
            // Subsection: format as {grade_id, section_id}
            const [gradeId, sectionId] = key.split(':');
            return { grade_id: gradeId, section_id: sectionId };
          } else {
            // Grade only: format as {grade_id, section_id: null}
            return { grade_id: key, section_id: null };
          }
        });

      // Also create grade string for backward compatibility
      const gradeNames = Array.from(selectedGrades)
        .filter(Boolean)
        .map(key => {
          const gradeId = key.includes(':') ? key.split(':')[0] : key;
          const grade = grades.find(g => g.id === gradeId);
          if (grade) {
            if (key.includes(':')) {
              const sectionId = key.split(':')[1];
              const section = gradeSections.find(s => s.id === sectionId);
              return section ? `${grade.name} ${section.name}` : grade.name;
            }
            return grade.name;
          }
          return null;
        })
        .filter(Boolean);
      const gradeString = gradeNames.length > 0 ? gradeNames.join(', ') : null;

      const updatePayload = {
        first_name: teacherFormData.first_name,
        last_name: teacherFormData.last_name,
        email: teacherFormData.email,
        phone: teacherFormData.phone || null,
        teacher_role: teacherFormData.teacher_role && teacherFormData.teacher_role.trim() !== '' ? teacherFormData.teacher_role : null,
        subject: teacherFormData.subject || null,
        supervisor_id: teacherFormData.supervisor_id || null,
        grade: gradeString,
        teachers_grade_ids: teachersGradeIds.length > 0 ? teachersGradeIds : null,
        is_active: teacherFormData.is_active
      };

      // Only update password if a new one is provided
      if (newPassword && newPassword.trim() !== '') {
        updatePayload.password = newPassword.trim();
      }

      const { error } = await supabase
        .from('users')
        .update(updatePayload)
        .eq('id', editingTeacher.id);

      if (error) {
        const friendlyMessage = getFriendlyErrorMessage(error, 'updating teacher');
        showNotification('error', 'Error Updating Teacher', friendlyMessage);
        return;
      }

      const passwordMsg = newPassword && newPassword.trim() !== '' ? ' Password has been updated.' : '';
      showNotification('success', 'Teacher Updated', `Teacher information updated successfully.${passwordMsg}`);
      setEditingTeacher(null);
      setShowAddModal(false);
      setInitialFormData(null);
      setInitialSelectedGrades(null);
      setNewPassword(''); // Clear password field after successful update
      onRefresh();
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err, 'updating teacher');
      showNotification('error', 'Error Updating Teacher', friendlyMessage);
    }
  };

  const handleDeleteTeacher = async () => {
    if (!teacherToDelete) return;

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', teacherToDelete);

      if (error) throw error;

      showNotification('success', 'Teacher Deleted', 'Teacher deleted successfully');
      setShowDeleteConfirm(false);
      setTeacherToDelete(null);
      onRefresh();
    } catch (err) {
      showNotification('error', 'Error Deleting Teacher', err.message);
      setShowDeleteConfirm(false);
      setTeacherToDelete(null);
    }
  };

  const confirmDeleteTeacher = (teacherId) => {
    setTeacherToDelete(teacherId);
    setShowDeleteConfirm(true);
  };

  const cancelDeleteTeacher = () => {
    setShowDeleteConfirm(false);
    setTeacherToDelete(null);
  };

  const startEdit = (teacher) => {
    setEditingTeacher(teacher);
    const formData = {
      first_name: teacher.first_name || '',
      last_name: teacher.last_name || '',
      email: teacher.email || '',
      phone: teacher.phone || '',
      teacher_role: teacher.teacher_role || '',
      subject: teacher.subject || '',
      supervisor_id: teacher.supervisor_id || '',
      is_active: teacher.is_active !== false
    };
    setTeacherFormData(formData);
    setInitialFormData(JSON.parse(JSON.stringify(formData)));
    setNewPassword(teacher.password || ''); // Load existing password when editing

    // Parse existing grades (from teachers_grade_ids or fallback to grade string)
    const gradeSet = parseGradesToSet(teacher);
    setSelectedGrades(gradeSet);
    setInitialSelectedGrades(new Set(gradeSet));

    setShowAddModal(true);
  };

  return (
    <div className="teachers-tab">
      <div className="tab-section-header" style={{ marginBottom: '20px' }}>
        <h2>Teachers ({teachers.length})</h2>
        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-container" style={{ minWidth: '220px' }}>
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isSearching && (
              <div className="loading-inline" style={{ margin: '0 0 0 8px' }}></div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              style={{
                padding: '10px 35px 10px 12px',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                outline: 'none',
                backgroundColor: '#f8f9fa',
                fontSize: '14px',
                color: '#333',
                minWidth: '150px',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                backgroundSize: '16px'
              }}
            >
              <option value="">All Roles</option>
              {teacherRoles.filter(r => r.value).map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>

            <select
              value={filterSubjectId}
              onChange={(e) => setFilterSubjectId(e.target.value)}
              style={{
                padding: '10px 35px 10px 12px',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                outline: 'none',
                backgroundColor: '#f8f9fa',
                fontSize: '14px',
                color: '#333',
                minWidth: '150px',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                backgroundSize: '16px'
              }}
            >
              <option value="">All Subjects</option>
              {subjects.filter(s => s.is_active).map(subject => (
                <option key={subject.id} value={subject.name}>{subject.name}</option>
              ))}
            </select>

            <select
              value={filterSubsectionId ? `${filterGradeId}:${filterSubsectionId}` : filterGradeId}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) {
                  setFilterGradeId('');
                  setFilterSubsectionId('');
                } else if (val.includes(':')) {
                  const [gId, sId] = val.split(':');
                  setFilterGradeId(gId);
                  setFilterSubsectionId(sId);
                } else {
                  setFilterGradeId(val);
                  setFilterSubsectionId('');
                }
              }}
              style={{
                padding: '10px 35px 10px 12px',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                outline: 'none',
                backgroundColor: '#f8f9fa',
                fontSize: '14px',
                color: '#333',
                minWidth: '150px',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                backgroundSize: '16px'
              }}
            >
              <option value="">All Grades</option>
              {buildGradeSectionOptions().map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>

            {(searchTerm || filterRole || filterSubjectId || filterGradeId) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterRole('');
                  setFilterSubjectId('');
                  setFilterGradeId('');
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #dc3545',
                  backgroundColor: '#fff',
                  color: '#dc3545',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#fff5f5'}
                onMouseLeave={(e) => e.target.style.background = '#fff'}
              >
                Clear
              </button>
            )}
          </div>

          <button className="add-btn" onClick={() => {
            setEditingTeacher(null);
            const emptyForm = {
              first_name: '', last_name: '', email: '', phone: '', teacher_role: '', subject: '', supervisor_id: '', is_active: true
            };
            setTeacherFormData(emptyForm);
            setInitialFormData(JSON.parse(JSON.stringify(emptyForm)));
            setNewPassword(''); // Clear password when adding new teacher
            const emptyGrades = new Set();
            setSelectedGrades(emptyGrades);
            setInitialSelectedGrades(new Set(emptyGrades));
            setShowAddModal(true);
          }}>
            + Add Teacher
          </button>
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>User ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {teachers.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {isSearching ? 'Searching...' : 'No teachers found matching your criteria'}
                </td>
              </tr>
            ) : (
              teachers.map(teacher => (
                <tr key={teacher.id}>
                  <td>{teacher.user_id}</td>
                  <td>{teacher.full_name || `${teacher.first_name} ${teacher.last_name}`}</td>
                  <td>{teacher.email || '—'}</td>
                  <td>{teacher.teacher_role || '—'}</td>
                  <td>{teacher.subject || '—'}</td>
                  <td>
                    <span className={`status-badge ${teacher.is_active ? 'active' : 'inactive'}`}>
                      {teacher.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="edit-action-btn" onClick={() => startEdit(teacher)}>Edit</button>
                      <button className="delete-action-btn" onClick={() => confirmDeleteTeacher(teacher.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Discard Changes Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="unsaved-changes-overlay" onClick={cancelDiscard}>
          <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-changes-header">
              <div className="unsaved-changes-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Unsaved Changes</h2>
            </div>
            <div className="unsaved-changes-body">
              <p className="unsaved-changes-message">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>
            <div className="unsaved-changes-actions">
              <button className="unsaved-changes-cancel-btn" onClick={cancelDiscard}>
                Cancel
              </button>
              <button className="unsaved-changes-discard-btn" onClick={confirmDiscard}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Teacher Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="school-detail-teachers-delete-overlay" onClick={cancelDeleteTeacher}>
          <div className="school-detail-teachers-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="school-detail-teachers-delete-header">
              <div className="school-detail-teachers-delete-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Delete Teacher</h2>
            </div>
            <div className="school-detail-teachers-delete-body">
              <p className="school-detail-teachers-delete-message">
                Are you sure you want to delete this teacher? This action cannot be undone.
              </p>
            </div>
            <div className="school-detail-teachers-delete-actions">
              <button className="school-detail-teachers-delete-cancel-btn" onClick={cancelDeleteTeacher}>
                Cancel
              </button>
              <button className="school-detail-teachers-delete-confirm-btn" onClick={handleDeleteTeacher}>
                Delete Teacher
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => handleOverlayClick(() => {
          setShowAddModal(false);
          setEditingTeacher(null);
          setNewPassword(''); // Clear password on close
        })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}</h2>
              <button className="modal-close" onClick={() => handleOverlayClick(() => {
                setShowAddModal(false);
                setEditingTeacher(null);
                setNewPassword(''); // Clear password on close
              })}>×</button>
            </div>
            <form onSubmit={editingTeacher ? handleUpdateTeacher : handleAddTeacher} className="user-form">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={teacherFormData.first_name}
                  onChange={(e) => setTeacherFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={teacherFormData.last_name}
                  onChange={(e) => setTeacherFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={teacherFormData.email}
                  onChange={(e) => setTeacherFormData(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={teacherFormData.phone}
                  onChange={(e) => setTeacherFormData(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Teacher Role</label>
                <select
                  value={teacherFormData.teacher_role}
                  onChange={(e) => setTeacherFormData(prev => ({ ...prev, teacher_role: e.target.value }))}
                >
                  {teacherRoles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Subject</label>
                <select
                  value={teacherFormData.subject}
                  onChange={(e) => setTeacherFormData(prev => ({ ...prev, subject: e.target.value }))}
                >
                  <option value="">No Subject</option>
                  {subjects.filter(s => s.is_active).map(s => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Supervisor</label>
                <select
                  value={teacherFormData.supervisor_id}
                  onChange={(e) => setTeacherFormData(prev => ({ ...prev, supervisor_id: e.target.value }))}
                >
                  <option value="">No Supervisor</option>
                  {teachers.filter(t => !editingTeacher || t.id !== editingTeacher.id).map(t => (
                    <option key={t.id} value={t.id}>{t.full_name || `${t.first_name} ${t.last_name}`}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ width: '100%' }}>
                <label>Grades (Select all grades this teacher can teach)</label>
                {grades && grades.length > 0 ? (
                  <div className="school-teacher-grade-checkbox-grid">
                    {buildGradeSectionOptions().map((option) => {
                      const isChecked = selectedGrades.has(option.key);
                      return (
                        <label
                          key={option.key}
                          className={`school-teacher-grade-checkbox ${isChecked ? 'checked' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleGradeToggle(option.key)}
                          />
                          <span>{option.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: '#666', fontSize: '0.875rem', marginTop: '8px' }}>No grades available. Please add grades for this school first.</p>
                )}
              </div>
              {editingTeacher && (
                <div className="form-group">
                  <label htmlFor="new_password">Change Password</label>
                  {/* Modern 6-square password UI */}
                  <div className="school-detail-teachers-password-squares-container">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <div
                        key={index}
                        className={`school-detail-teachers-password-square ${newPassword.length > index ? 'filled' : ''} ${newPassword.length === index ? 'active' : ''}`}
                        onClick={handlePasswordSquareClick}
                      >
                        {newPassword[index] || ''}
                      </div>
                    ))}
                  </div>
                  {/* Hidden input that actually controls the password value */}
                  <input
                    type="password"
                    id="school-detail-teachers-password-input"
                    name="new_password"
                    value={newPassword}
                    onChange={handlePasswordChange}
                    className="school-detail-teachers-password-hidden-input"
                    placeholder="Enter 6-digit password (numbers only)"
                    maxLength="6"
                    autoComplete="new-password"
                  />
                  <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
                    Password must be 6 digits (numbers only). Current password is shown. Edit to change it, or leave as-is to keep the same password.
                  </small>
                </div>
              )}
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={teacherFormData.is_active}
                    onChange={(e) => setTeacherFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span>Active</span>
                </label>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => handleOverlayClick(() => {
                  setShowAddModal(false);
                  setEditingTeacher(null);
                  setNewPassword(''); // Clear password on cancel
                  setSelectedGrades(new Set());
                })}>Cancel</button>
                <button type="submit" className="save-btn">{editingTeacher ? 'Update' : 'Add'} Teacher</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Grades Tab Component
const GradesTab = ({ grades, gradeSections, schoolId, onRefresh, showNotification }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [gradeFormData, setGradeFormData] = useState({ name: '', order_index: 0, is_active: true, level: 1 });

  // Subsections management state
  const [showSubsectionsModal, setShowSubsectionsModal] = useState(false);
  const [selectedGradeForSections, setSelectedGradeForSections] = useState(null);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionFormData, setSectionFormData] = useState({ name: '', order_index: 0, is_active: true });

  // Unsaved changes state for grades
  const [showDiscardConfirmGrade, setShowDiscardConfirmGrade] = useState(false);
  const [pendingCloseActionGrade, setPendingCloseActionGrade] = useState(null);
  const [initialGradeFormData, setInitialGradeFormData] = useState(null);

  // Unsaved changes state for sections
  const [showDiscardConfirmSection, setShowDiscardConfirmSection] = useState(false);
  const [pendingCloseActionSection, setPendingCloseActionSection] = useState(null);
  const [initialSectionFormData, setInitialSectionFormData] = useState(null);

  // Delete confirmation state
  const [showDeleteGradeConfirm, setShowDeleteGradeConfirm] = useState(false);
  const [gradeToDelete, setGradeToDelete] = useState(null);
  const [showDeleteSectionConfirm, setShowDeleteSectionConfirm] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);

  // Check if grade form has been modified
  const hasGradeFormChanges = () => {
    if (!initialGradeFormData) return false;
    return JSON.stringify(gradeFormData) !== JSON.stringify(initialGradeFormData);
  };

  // Handle grade overlay click with unsaved changes check
  const handleGradeOverlayClick = (closeAction) => {
    if (hasGradeFormChanges()) {
      setPendingCloseActionGrade(() => closeAction);
      setShowDiscardConfirmGrade(true);
    } else {
      closeAction();
    }
  };

  // Confirm discard grade changes
  const confirmDiscardGrade = () => {
    if (pendingCloseActionGrade) {
      pendingCloseActionGrade();
      setPendingCloseActionGrade(null);
    }
    setShowDiscardConfirmGrade(false);
    setInitialGradeFormData(null);
  };

  // Cancel discard grade
  const cancelDiscardGrade = () => {
    setShowDiscardConfirmGrade(false);
    setPendingCloseActionGrade(null);
  };

  // Check if section form has been modified
  const hasSectionFormChanges = () => {
    if (!initialSectionFormData) return false;
    return JSON.stringify(sectionFormData) !== JSON.stringify(initialSectionFormData);
  };

  // Handle section overlay click with unsaved changes check
  const handleSectionOverlayClick = (closeAction) => {
    if (hasSectionFormChanges()) {
      setPendingCloseActionSection(() => closeAction);
      setShowDiscardConfirmSection(true);
    } else {
      closeAction();
    }
  };

  // Confirm discard section changes
  const confirmDiscardSection = () => {
    if (pendingCloseActionSection) {
      pendingCloseActionSection();
      setPendingCloseActionSection(null);
    }
    setShowDiscardConfirmSection(false);
    setInitialSectionFormData(null);
  };

  // Cancel discard section
  const cancelDiscardSection = () => {
    setShowDiscardConfirmSection(false);
    setPendingCloseActionSection(null);
  };

  // Set initial grade form data when modal opens
  useEffect(() => {
    if (showAddModal) {
      setInitialGradeFormData(JSON.parse(JSON.stringify(gradeFormData)));
    } else {
      setInitialGradeFormData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddModal]);

  // Set initial section form data when modal opens
  useEffect(() => {
    if (showAddSectionModal) {
      setInitialSectionFormData(JSON.parse(JSON.stringify(sectionFormData)));
    } else {
      setInitialSectionFormData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddSectionModal]);

  const handleAddGrade = async (e) => {
    e.preventDefault();
    try {
      const maxOrder = grades.length > 0 ? Math.max(...grades.map(g => g.order_index)) : -1;

      const { error } = await supabase
        .from('grades')
        .insert([{
          school_id: schoolId,
          name: gradeFormData.name,
          order_index: maxOrder + 1,
          is_active: gradeFormData.is_active,
          level: gradeFormData.level || 1
        }]);

      if (error) throw error;

      showNotification('success', 'Grade Added', 'Grade added successfully');
      setShowAddModal(false);
      setGradeFormData({ name: '', order_index: 0, is_active: true, level: 1 });
      setInitialGradeFormData(null);
      onRefresh();
    } catch (err) {
      showNotification('error', 'Error Adding Grade', err.message);
    }
  };

  const handleUpdateGrade = async (e) => {
    e.preventDefault();
    if (!editingGrade) return;

    try {
      const { error } = await supabase
        .from('grades')
        .update({
          name: gradeFormData.name,
          order_index: gradeFormData.order_index,
          is_active: gradeFormData.is_active,
          level: gradeFormData.level || 1
        })
        .eq('id', editingGrade.id);

      if (error) throw error;

      showNotification('success', 'Grade Updated', 'Grade updated successfully');
      setEditingGrade(null);
      setShowAddModal(false);
      setInitialGradeFormData(null);
      onRefresh();
    } catch (err) {
      showNotification('error', 'Error Updating Grade', err.message);
    }
  };

  const handleDeleteGrade = async () => {
    if (!gradeToDelete) return;

    try {
      // First delete all sections for this grade
      const { error: sectionsError } = await supabase
        .from('grade_sections')
        .delete()
        .eq('grade_id', gradeToDelete);

      if (sectionsError) throw sectionsError;

      // Then delete the grade
      const { error } = await supabase
        .from('grades')
        .delete()
        .eq('id', gradeToDelete);

      if (error) throw error;

      showNotification('success', 'Grade Deleted', 'Grade deleted successfully');
      setShowDeleteGradeConfirm(false);
      setGradeToDelete(null);
      onRefresh();
    } catch (err) {
      showNotification('error', 'Error Deleting Grade', err.message);
      setShowDeleteGradeConfirm(false);
      setGradeToDelete(null);
    }
  };

  const confirmDeleteGrade = (gradeId) => {
    setGradeToDelete(gradeId);
    setShowDeleteGradeConfirm(true);
  };

  const cancelDeleteGrade = () => {
    setShowDeleteGradeConfirm(false);
    setGradeToDelete(null);
  };

  // Get subsections for a specific grade
  const getSectionsForGrade = (gradeId) => {
    return gradeSections.filter(section => section.grade_id === gradeId);
  };

  // Handle opening subsections modal
  const handleManageSubsections = (grade) => {
    setSelectedGradeForSections(grade);
    setShowSubsectionsModal(true);
  };

  // Handle adding a new section
  const handleAddSection = async (e) => {
    e.preventDefault();
    if (!selectedGradeForSections) return;

    try {
      const sectionsForGrade = getSectionsForGrade(selectedGradeForSections.id);
      const maxOrder = sectionsForGrade.length > 0 ? Math.max(...sectionsForGrade.map(s => s.order_index)) : -1;

      const { error } = await supabase
        .from('grade_sections')
        .insert([{
          grade_id: selectedGradeForSections.id,
          name: sectionFormData.name,
          order_index: maxOrder + 1,
          is_active: sectionFormData.is_active
        }]);

      if (error) throw error;

      showNotification('success', 'Subsection Added', 'Subsection added successfully');
      setShowAddSectionModal(false);
      setSectionFormData({ name: '', order_index: 0, is_active: true });
      setInitialSectionFormData(null);
      onRefresh();
    } catch (err) {
      showNotification('error', 'Error Adding Subsection', err.message);
    }
  };

  // Handle updating a section
  const handleUpdateSection = async (e) => {
    e.preventDefault();
    if (!editingSection) return;

    try {
      const { error } = await supabase
        .from('grade_sections')
        .update({
          name: sectionFormData.name,
          order_index: sectionFormData.order_index,
          is_active: sectionFormData.is_active
        })
        .eq('id', editingSection.id);

      if (error) throw error;

      showNotification('success', 'Subsection Updated', 'Subsection updated successfully');
      setEditingSection(null);
      setShowAddSectionModal(false);
      setSectionFormData({ name: '', order_index: 0, is_active: true });
      setInitialSectionFormData(null);
      onRefresh();
    } catch (err) {
      showNotification('error', 'Error Updating Subsection', err.message);
    }
  };

  // Handle deleting a section
  const handleDeleteSection = async () => {
    if (!sectionToDelete) return;

    try {
      const { error } = await supabase
        .from('grade_sections')
        .delete()
        .eq('id', sectionToDelete);

      if (error) throw error;

      showNotification('success', 'Subsection Deleted', 'Subsection deleted successfully');
      setShowDeleteSectionConfirm(false);
      setSectionToDelete(null);
      onRefresh();
    } catch (err) {
      showNotification('error', 'Error Deleting Subsection', err.message);
      setShowDeleteSectionConfirm(false);
      setSectionToDelete(null);
    }
  };

  const confirmDeleteSection = (sectionId) => {
    setSectionToDelete(sectionId);
    setShowDeleteSectionConfirm(true);
  };

  const cancelDeleteSection = () => {
    setShowDeleteSectionConfirm(false);
    setSectionToDelete(null);
  };

  return (
    <div className="grades-tab">
      <div className="tab-section-header">
        <h2>Grades ({grades.length})</h2>
        <button className="add-btn" onClick={() => {
          setEditingGrade(null);
          const emptyForm = { name: '', order_index: 0, is_active: true, level: 1 };
          setGradeFormData(emptyForm);
          setInitialGradeFormData(JSON.parse(JSON.stringify(emptyForm)));
          setShowAddModal(true);
        }}>
          + Add Grade
        </button>
      </div>

      <div className="grades-list">
        {grades.length === 0 ? (
          <div className="no-data">No grades found</div>
        ) : (
          grades.map(grade => {
            const sectionsForGrade = getSectionsForGrade(grade.id);
            return (
              <div key={grade.id} className="grade-item">
                <div className="grade-info">
                  <div className="grade-header">
                    <h3>{grade.name}</h3>
                    <span className={`status-badge ${grade.is_active ? 'active' : 'inactive'}`}>
                      {grade.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {sectionsForGrade.length > 0 && (
                    <div className="grade-subsections-preview">
                      <span className="subsections-count">{sectionsForGrade.length} subsection{sectionsForGrade.length !== 1 ? 's' : ''}</span>
                      <div className="subsections-list-preview">
                        {sectionsForGrade.slice(0, 3).map(section => (
                          <span key={section.id} className="subsection-tag">
                            {section.name}
                          </span>
                        ))}
                        {sectionsForGrade.length > 3 && (
                          <span className="subsection-tag">+{sectionsForGrade.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="action-buttons">
                  <button className="manage-subsections-btn" onClick={() => handleManageSubsections(grade)}>
                    Manage Subsections ({sectionsForGrade.length})
                  </button>
                  <button className="edit-action-btn" onClick={() => {
                    setEditingGrade(grade);
                    const formData = {
                      name: grade.name,
                      order_index: grade.order_index,
                      is_active: grade.is_active,
                      level: grade.level || 1
                    };
                    setGradeFormData(formData);
                    setInitialGradeFormData(JSON.parse(JSON.stringify(formData)));
                    setShowAddModal(true);
                  }}>Edit</button>
                  <button className="delete-action-btn" onClick={() => confirmDeleteGrade(grade.id)}>Delete</button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Discard Changes Confirmation Dialog for Grades */}
      {showDiscardConfirmGrade && (
        <div className="unsaved-changes-overlay" onClick={cancelDiscardGrade}>
          <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-changes-header">
              <div className="unsaved-changes-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Unsaved Changes</h2>
            </div>
            <div className="unsaved-changes-body">
              <p className="unsaved-changes-message">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>
            <div className="unsaved-changes-actions">
              <button className="unsaved-changes-cancel-btn" onClick={cancelDiscardGrade}>
                Cancel
              </button>
              <button className="unsaved-changes-discard-btn" onClick={confirmDiscardGrade}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => handleGradeOverlayClick(() => {
          setShowAddModal(false);
          setEditingGrade(null);
        })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingGrade ? 'Edit Grade' : 'Add New Grade'}</h2>
              <button className="modal-close" onClick={() => handleGradeOverlayClick(() => {
                setShowAddModal(false);
                setEditingGrade(null);
              })}>×</button>
            </div>
            <form onSubmit={editingGrade ? handleUpdateGrade : handleAddGrade} className="user-form">
              <div className="form-group">
                <label>Grade Name *</label>
                <input
                  type="text"
                  value={gradeFormData.name}
                  onChange={(e) => setGradeFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              {/* Order Index hidden from UI
              <div className="form-group">
                <label>Order Index</label>
                <input
                  type="number"
                  value={gradeFormData.order_index}
                  onChange={(e) => setGradeFormData(prev => ({ ...prev, order_index: parseInt(e.target.value) || 0 }))}
                />
              </div>
              */}
              <div className="form-group">
                <label>Level (1-12)</label>
                <input
                  type="number"
                  value={gradeFormData.level || 1}
                  onChange={(e) => setGradeFormData(prev => ({ ...prev, level: parseInt(e.target.value) || 1 }))}
                  min="1"
                  max="12"
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={gradeFormData.is_active}
                    onChange={(e) => setGradeFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span>Active</span>
                </label>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => handleGradeOverlayClick(() => {
                  setShowAddModal(false);
                  setEditingGrade(null);
                })}>Cancel</button>
                <button type="submit" className="save-btn">{editingGrade ? 'Update' : 'Add'} Grade</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subsections Management Modal */}
      {showSubsectionsModal && selectedGradeForSections && (
        <div className="modal-overlay" onClick={() => {
          setShowSubsectionsModal(false);
          setSelectedGradeForSections(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Subsections - {selectedGradeForSections.name}</h2>
              <button className="modal-close" onClick={() => {
                setShowSubsectionsModal(false);
                setSelectedGradeForSections(null);
              }}>×</button>
            </div>
            <div className="subsections-management">
              <div className="subsections-list-header">
                <h3>Subsections</h3>
                <button className="add-btn" onClick={() => {
                  setEditingSection(null);
                  const emptyForm = { name: '', order_index: 0, is_active: true };
                  setSectionFormData(emptyForm);
                  setInitialSectionFormData(JSON.parse(JSON.stringify(emptyForm)));
                  setShowAddSectionModal(true);
                }}>
                  + Add Subsection
                </button>
              </div>
              <div className="subsections-list">
                {getSectionsForGrade(selectedGradeForSections.id).length === 0 ? (
                  <div className="no-data">No subsections found</div>
                ) : (
                  getSectionsForGrade(selectedGradeForSections.id).map(section => (
                    <div key={section.id} className="subsection-item">
                      <div className="subsection-info">
                        <h4>{section.name}</h4>
                        <span className={`status-badge ${section.is_active ? 'active' : 'inactive'}`}>
                          {section.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="action-buttons">
                        <button className="edit-action-btn" onClick={() => {
                          setEditingSection(section);
                          const formData = {
                            name: section.name,
                            order_index: section.order_index,
                            is_active: section.is_active
                          };
                          setSectionFormData(formData);
                          setInitialSectionFormData(JSON.parse(JSON.stringify(formData)));
                          setShowAddSectionModal(true);
                        }}>Edit</button>
                        <button className="delete-action-btn" onClick={() => confirmDeleteSection(section.id)}>Delete</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discard Changes Confirmation Dialog for Sections */}
      {showDiscardConfirmSection && (
        <div className="unsaved-changes-overlay" onClick={cancelDiscardSection}>
          <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-changes-header">
              <div className="unsaved-changes-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Unsaved Changes</h2>
            </div>
            <div className="unsaved-changes-body">
              <p className="unsaved-changes-message">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>
            <div className="unsaved-changes-actions">
              <button className="unsaved-changes-cancel-btn" onClick={cancelDiscardSection}>
                Cancel
              </button>
              <button className="unsaved-changes-discard-btn" onClick={confirmDiscardSection}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Subsection Modal */}
      {showAddSectionModal && selectedGradeForSections && (
        <div className="modal-overlay" onClick={() => handleSectionOverlayClick(() => {
          setShowAddSectionModal(false);
          setEditingSection(null);
        })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSection ? 'Edit Subsection' : 'Add New Subsection'}</h2>
              <button className="modal-close" onClick={() => handleSectionOverlayClick(() => {
                setShowAddSectionModal(false);
                setEditingSection(null);
              })}>×</button>
            </div>
            <form onSubmit={editingSection ? handleUpdateSection : handleAddSection} className="user-form">
              <div className="form-group">
                <label>Subsection Name *</label>
                <input
                  type="text"
                  value={sectionFormData.name}
                  onChange={(e) => setSectionFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Order Index</label>
                <input
                  type="number"
                  value={sectionFormData.order_index}
                  onChange={(e) => setSectionFormData(prev => ({ ...prev, order_index: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={sectionFormData.is_active}
                    onChange={(e) => setSectionFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span>Active</span>
                </label>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => handleSectionOverlayClick(() => {
                  setShowAddSectionModal(false);
                  setEditingSection(null);
                })}>Cancel</button>
                <button type="submit" className="save-btn">{editingSection ? 'Update' : 'Add'} Subsection</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Grade Confirmation Dialog */}
      {showDeleteGradeConfirm && (
        <div className="school-detail-grades-delete-overlay" onClick={cancelDeleteGrade}>
          <div className="school-detail-grades-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="school-detail-grades-delete-header">
              <div className="school-detail-grades-delete-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Delete Grade</h2>
            </div>
            <div className="school-detail-grades-delete-body">
              <p className="school-detail-grades-delete-message">
                Are you sure you want to delete this grade? This will also delete all subsections for this grade. This action cannot be undone.
              </p>
            </div>
            <div className="school-detail-grades-delete-actions">
              <button className="school-detail-grades-delete-cancel-btn" onClick={cancelDeleteGrade}>
                Cancel
              </button>
              <button className="school-detail-grades-delete-confirm-btn" onClick={handleDeleteGrade}>
                Delete Grade
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Section Confirmation Dialog */}
      {showDeleteSectionConfirm && (
        <div className="school-detail-sections-delete-overlay" onClick={cancelDeleteSection}>
          <div className="school-detail-sections-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="school-detail-sections-delete-header">
              <div className="school-detail-sections-delete-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Delete Subsection</h2>
            </div>
            <div className="school-detail-sections-delete-body">
              <p className="school-detail-sections-delete-message">
                Are you sure you want to delete this subsection? This action cannot be undone.
              </p>
            </div>
            <div className="school-detail-sections-delete-actions">
              <button className="school-detail-sections-delete-cancel-btn" onClick={cancelDeleteSection}>
                Cancel
              </button>
              <button className="school-detail-sections-delete-confirm-btn" onClick={handleDeleteSection}>
                Delete Subsection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Subjects Tab Component
const SubjectsTab = ({ subjects, schoolId, onRefresh, showNotification }) => {
  // Modal and form state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [subjectFormData, setSubjectFormData] = useState({ name: '' });
  
  // Unsaved changes state
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [subjectToDelete, setSubjectToDelete] = useState(null);

  // Check if form has been modified
  const hasFormChanges = () => {
    if (!initialFormData) return false;
    return JSON.stringify(subjectFormData) !== JSON.stringify(initialFormData);
  };

  // Handle overlay click with unsaved changes check
  const handleOverlayClick = (closeAction) => {
    if (hasFormChanges()) {
      setPendingCloseAction(() => closeAction);
      setShowDiscardConfirm(true);
    } else {
      closeAction();
    }
  };

  // Confirm discard changes
  const confirmDiscard = () => {
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
    setShowDiscardConfirm(false);
    setInitialFormData(null);
  };

  // Cancel discard
  const cancelDiscard = () => {
    setShowDiscardConfirm(false);
    setPendingCloseAction(null);
  };

  // Set initial form data when modal opens
  useEffect(() => {
    if (showAddModal) {
      setInitialFormData(JSON.parse(JSON.stringify(subjectFormData)));
    } else {
      setInitialFormData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddModal]);

  const handleAddSubject = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('subjects')
        .insert([{
          school_id: schoolId,
          name: subjectFormData.name
        }]);

      if (error) throw error;

      showNotification('success', 'Subject Added', 'Subject added successfully');
      setShowAddModal(false);
      setSubjectFormData({ name: '' });
      setInitialFormData(null);
      onRefresh();
    } catch (err) {
      showNotification('error', 'Error Adding Subject', err.message);
    }
  };

  const handleUpdateSubject = async (e) => {
    e.preventDefault();
    if (!editingSubject) return;

    try {
      const { error } = await supabase
        .from('subjects')
        .update({ name: subjectFormData.name })
        .eq('id', editingSubject.id);

      if (error) throw error;

      showNotification('success', 'Subject Updated', 'Subject updated successfully');
      setEditingSubject(null);
      setShowAddModal(false);
      setInitialFormData(null);
      onRefresh();
    } catch (err) {
      showNotification('error', 'Error Updating Subject', err.message);
    }
  };

  const handleDeleteSubject = async () => {
    if (!subjectToDelete) return;

    try {
      const { error } = await supabase
        .from('subjects')
        .delete()
        .eq('id', subjectToDelete);

      if (error) throw error;

      showNotification('success', 'Subject Deleted', 'Subject deleted successfully');
      setShowDeleteConfirm(false);
      setSubjectToDelete(null);
      onRefresh();
    } catch (err) {
      showNotification('error', 'Error Deleting Subject', err.message);
      setShowDeleteConfirm(false);
      setSubjectToDelete(null);
    }
  };

  const confirmDeleteSubject = (subjectId) => {
    setSubjectToDelete(subjectId);
    setShowDeleteConfirm(true);
  };

  const cancelDeleteSubject = () => {
    setShowDeleteConfirm(false);
    setSubjectToDelete(null);
  };

  return (
    <div className="subjects-tab">
      <div className="tab-section-header">
        <h2>Subjects ({subjects.length})</h2>
        <button className="add-btn" onClick={() => {
          setEditingSubject(null);
          const emptyForm = { name: '' };
          setSubjectFormData(emptyForm);
          setInitialFormData(JSON.parse(JSON.stringify(emptyForm)));
          setShowAddModal(true);
        }}>
          + Add Subject
        </button>
      </div>

      <div className="subjects-list">
        {subjects.length === 0 ? (
          <div className="no-data">No subjects found</div>
        ) : (
          subjects.map(subject => (
            <div key={subject.id} className="subject-item">
              <div className="subject-info">
                <h3>{subject.name}</h3>
              </div>
              <div className="action-buttons">
                <button className="edit-action-btn" onClick={() => {
                  setEditingSubject(subject);
                  const formData = { name: subject.name };
                  setSubjectFormData(formData);
                  setInitialFormData(JSON.parse(JSON.stringify(formData)));
                  setShowAddModal(true);
                }}>Edit</button>
                <button className="delete-action-btn" onClick={() => confirmDeleteSubject(subject.id)}>Delete</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Discard Changes Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="unsaved-changes-overlay" onClick={cancelDiscard}>
          <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-changes-header">
              <div className="unsaved-changes-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Unsaved Changes</h2>
            </div>
            <div className="unsaved-changes-body">
              <p className="unsaved-changes-message">
                You have unsaved changes. Are you sure you want to discard them?
              </p>
            </div>
            <div className="unsaved-changes-actions">
              <button className="unsaved-changes-cancel-btn" onClick={cancelDiscard}>
                Cancel
              </button>
              <button className="unsaved-changes-discard-btn" onClick={confirmDiscard}>
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Subject Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="school-detail-subjects-delete-overlay" onClick={cancelDeleteSubject}>
          <div className="school-detail-subjects-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="school-detail-subjects-delete-header">
              <div className="school-detail-subjects-delete-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2>Delete Subject</h2>
            </div>
            <div className="school-detail-subjects-delete-body">
              <p className="school-detail-subjects-delete-message">
                Are you sure you want to delete this subject? This action cannot be undone.
              </p>
            </div>
            <div className="school-detail-subjects-delete-actions">
              <button className="school-detail-subjects-delete-cancel-btn" onClick={cancelDeleteSubject}>
                Cancel
              </button>
              <button className="school-detail-subjects-delete-confirm-btn" onClick={handleDeleteSubject}>
                Delete Subject
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => handleOverlayClick(() => {
          setShowAddModal(false);
          setEditingSubject(null);
        })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSubject ? 'Edit Subject' : 'Add New Subject'}</h2>
              <button className="modal-close" onClick={() => handleOverlayClick(() => {
                setShowAddModal(false);
                setEditingSubject(null);
              })}>×</button>
            </div>
            <form onSubmit={editingSubject ? handleUpdateSubject : handleAddSubject} className="user-form">
              <div className="form-group">
                <label>Subject Name *</label>
                <input
                  type="text"
                  value={subjectFormData.name}
                  onChange={(e) => setSubjectFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => handleOverlayClick(() => {
                  setShowAddModal(false);
                  setEditingSubject(null);
                })}>Cancel</button>
                <button type="submit" className="save-btn">{editingSubject ? 'Update' : 'Add'} Subject</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Providers Tab Component
const ProvidersTab = ({ school, providers, selectedProviders, onRefresh, isSubmitting, showNotification }) => {
  const [localSelected, setLocalSelected] = useState(selectedProviders);

  useEffect(() => {
    setLocalSelected(selectedProviders);
  }, [selectedProviders]);

  const handleToggle = (providerId) => {
    setLocalSelected(prev =>
      prev.includes(providerId)
        ? prev.filter(id => id !== providerId)
        : [...prev, providerId]
    );
  };

  const handleSelectAll = () => {
    if (providers.length === 0) return;
    // If all are selected, deselect all; otherwise select all
    const allSelected = providers.every(p => localSelected.includes(p.id));
    if (allSelected) {
      setLocalSelected([]);
    } else {
      setLocalSelected(providers.map(p => p.id));
    }
  };

  const handleSave = async () => {
    try {
      const { error } = await supabase
        .from('schools')
        .update({ accessible_providers: localSelected })
        .eq('id', school.id);

      if (error) throw error;

      showNotification('success', 'Providers Updated', 'Learning providers updated successfully');
      onRefresh(); // Refresh parent data
    } catch (err) {
      showNotification('error', 'Error Updating Providers', err.message);
    }
  };

  return (
    <div className="providers-tab">
      <div className="tab-section-header">
        <h2>Learning Providers</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button 
            className="select-all-providers-btn" 
            onClick={handleSelectAll}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid #3b82f6',
              backgroundColor: providers.length > 0 && providers.every(p => localSelected.includes(p.id)) ? '#3b82f6' : '#fff',
              color: providers.length > 0 && providers.every(p => localSelected.includes(p.id)) ? '#fff' : '#3b82f6',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!(providers.length > 0 && providers.every(p => localSelected.includes(p.id)))) {
                e.target.style.background = '#f0f7ff';
              }
            }}
            onMouseLeave={(e) => {
              if (!(providers.length > 0 && providers.every(p => localSelected.includes(p.id)))) {
                e.target.style.background = '#fff';
              }
            }}
          >
            {providers.length > 0 && providers.every(p => localSelected.includes(p.id)) ? 'Deselect All' : 'Select All'}
          </button>
          <button className="save-btn" onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="providers-grid">
        {providers.length === 0 ? (
          <div className="no-data">No providers available</div>
        ) : (
          providers.map(provider => (
            <label key={provider.id} className="provider-checkbox-card">
              <input
                type="checkbox"
                checked={localSelected.includes(provider.id)}
                onChange={() => handleToggle(provider.id)}
              />
              <div className="provider-card-content">
                <h3>{provider.name}</h3>
                {provider.description && <p>{provider.description}</p>}
              </div>
            </label>
          ))
        )}
      </div>
    </div>
  );
};

export default SchoolDetail;

